// src/features/stock-info/api.ts
//
// Data fetcher for the Stock Info deep-dive page. Talks to the FastAPI
// stock_info router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the endpoint is reached only as an authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/stock-info/*` path here — adding
// `/api/v1` would double the prefix.

import { apiClient } from '../../api/client';

const BASE_URL = '/stock-info';

// Interface matching the Pydantic schema we built on the backend.
//
// CRITICAL: the backend serializes with `model_dump(by_alias=False,
// exclude_none=True)` in StockInfoService.get_overview — so the JSON keys
// are SNAKE_CASE (market_cap, company_name, changes_percentage,
// target_consensus), NOT the camelCase FMP aliases. And because
// `exclude_none=True` drops null fields, every field here is optional.
export interface StockOverview {
    symbol: string;
    quote: {
        symbol?: string;
        price?: number;
        changes_percentage?: number;
        change?: number;
        previous_close?: number;
        market_cap?: number;
        exchange?: string;
        name?: string;
        // ISO listing currency resolved server-side (explicit-first: the
        // /stable/profile currency, then venue/suffix fallback).
        currency?: string;
        // Average daily traded volume (share count) from /stable/profile.
        avg_volume?: number;
    };
    profile: {
        company_name?: string;
        sector?: string;
        industry?: string;
        description?: string;
        exchange?: string;
        // Enriched header stats sourced server-side from /stable/profile.
        beta?: number;
        isin?: string;
        // Trailing annual dividend per share (absolute, in listing currency).
        last_dividend?: number;
    };
    target: {
        target_consensus?: number;
        target_high?: number;
        target_low?: number;
        consensus?: string;
    };
    grades: {
        strong_buy?: number;
        buy?: number;
        hold?: number;
        sell?: number;
        strong_sell?: number;
        consensus?: string;
    };
    discount_premium_pct?: number | null;
}

export const fetchStockOverview = async (symbol: string): Promise<StockOverview> => {
    // Resolves to GET /api/v1/stock-info/{symbol}/overview (prefix from baseURL)
    const { data } = await apiClient.get<StockOverview>(`${BASE_URL}/${symbol}/overview`);
    return data;
};

// ---------------------------------------------------------------------------
// Phase 2 — Component Islands (chart, peers, financial tabs)
//
// NOTE: every fetcher below goes through the shared `apiClient` (axios), NOT
// the browser `fetch`. apiClient attaches the WordPress JWT and bakes the
// `/api/v1` prefix into baseURL — a raw `fetch('/stock-info/...')` would hit
// the SPA origin unauthenticated and 401.
// ---------------------------------------------------------------------------

// GET /stock-info/{symbol}/chart — one OHLCV bar per row (raw FMP keys, which
// are already lowercase: date/open/high/low/close/volume).
export interface ChartBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

// GET /stock-info/{symbol}/peers — Polars-ranked competitors. The backend
// service emits snake_case keys.
export interface PeerRecord {
    symbol: string;
    company_name: string;
    price: number;
    changes_percentage: number;
    market_cap: number;
}

// Financial tables (key-metrics / ratios / growth / estimates) are rendered
// GENERICALLY: the UI derives columns + the chartable metric list from the
// row keys at runtime. Key casing differs by endpoint — key-metrics is
// snake_case (schema-projected) while ratios/growth/estimates are raw FMP
// camelCase — so a permissive row shape covers all four and the components
// format headers dynamically.
export type FinancialRow = Record<string, string | number | null>;

// GET /stock-info/{symbol}/earnings — actual-vs-estimated EPS & revenue for
// the most recent reporting periods. The backend serializes EarningsSchema
// with `exclude_none=False`, so every field is always present (future
// quarters carry `null` actuals until results are reported).
export interface EarningsRow {
    date: string | null;
    eps_actual: number | null;
    eps_estimated: number | null;
    revenue_actual: number | null;
    revenue_estimated: number | null;
}

export const fetchIntradayChart = async (
    symbol: string,
    interval: string,
): Promise<ChartBar[]> => {
    const { data } = await apiClient.get<ChartBar[]>(`${BASE_URL}/${symbol}/chart`, {
        params: { interval },
    });
    return data;
};

// GET /stock-info/{symbol}/technical-indicators — one OHLCV bar per row PLUS
// the indicator value under a key named after the indicator (sma/ema/wma/
// rsi/adx). FMP keys are already lowercase, so an index signature captures
// whichever indicator column comes back on top of the OHLCV fields.
export interface TechnicalIndicatorBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    // The active indicator arrives under its own key (e.g. `sma`, `rsi`).
    [indicator: string]: number | string;
}

// Valid indicators exposed by the backend proxy (mirrors the FMP
// /stable/technical-indicators/{indicator} slugs).
export type TechnicalIndicator = 'sma' | 'ema' | 'wma' | 'rsi' | 'adx';

export const fetchTechnicalIndicator = async (
    symbol: string,
    params: {
        indicator: TechnicalIndicator;
        periodLength: number;
        timeframe: string;
        from?: string;
        to?: string;
    },
): Promise<TechnicalIndicatorBar[]> => {
    const { data } = await apiClient.get<TechnicalIndicatorBar[]>(
        `${BASE_URL}/${symbol}/technical-indicators`,
        {
            // Backend aliases these to FMP's exact param names
            // (indicator / periodLength / timeframe / from / to).
            params: {
                indicator: params.indicator,
                periodLength: params.periodLength,
                timeframe: params.timeframe,
                ...(params.from ? { from: params.from } : {}),
                ...(params.to ? { to: params.to } : {}),
            },
        },
    );
    return data;
};

export const fetchRankedPeers = async (symbol: string): Promise<PeerRecord[]> => {
    const { data } = await apiClient.get<PeerRecord[]>(`${BASE_URL}/${symbol}/peers`);
    return data;
};

export const fetchKeyMetrics = async (
    symbol: string,
    period: string,
): Promise<FinancialRow[]> => {
    const { data } = await apiClient.get<FinancialRow[]>(
        `${BASE_URL}/${symbol}/key-metrics`,
        { params: { period } },
    );
    return data;
};

export const fetchRatios = async (
    symbol: string,
    period: string,
): Promise<FinancialRow[]> => {
    const { data } = await apiClient.get<FinancialRow[]>(`${BASE_URL}/${symbol}/ratios`, {
        params: { period },
    });
    return data;
};

export const fetchGrowth = async (
    symbol: string,
    statement: string,
    period: string,
): Promise<FinancialRow[]> => {
    const { data } = await apiClient.get<FinancialRow[]>(`${BASE_URL}/${symbol}/growth`, {
        params: { statement, period },
    });
    return data;
};

export const fetchEstimates = async (
    symbol: string,
    period: string,
): Promise<FinancialRow[]> => {
    const { data } = await apiClient.get<FinancialRow[]>(
        `${BASE_URL}/${symbol}/estimates`,
        { params: { period } },
    );
    return data;
};

export const fetchEarnings = async (
    symbol: string,
    limit = 5,
): Promise<EarningsRow[]> => {
    const { data } = await apiClient.get<EarningsRow[]>(
        `${BASE_URL}/${symbol}/earnings`,
        { params: { limit } },
    );
    return data;
};

// GET /stock-info/{symbol}/share-float — free-float vs institutional split
// for the Share Float & Liquidity pie chart. Backend serializes snake_case
// with nulls dropped, so every field is optional. `free_float` and
// `institutional_float` are already percentages (e.g. 99.83 / 0.17).
export interface ShareFloat {
    symbol?: string;
    date?: string;
    free_float?: number;
    institutional_float?: number;
    float_shares?: number;
    outstanding_shares?: number;
}

// GET /stock-info/{symbol}/valuation-ratios — aggregated TTM valuation
// multiples for the ratios table. `dividend_yield` is a FRACTION (e.g.
// 0.0033 = 0.33%); the table multiplies by 100 for display. All optional
// because the backend drops null fields.
export interface ValuationRatios {
    symbol: string;
    pe_ratio?: number;
    pb_ratio?: number;
    ps_ratio?: number;
    pfcf_ratio?: number;
    dividend_yield?: number;
    beta?: number;
    peg_ratio?: number;
    forward_pe_current?: number;
    forward_pe_next?: number;
    forward_pe_current_year?: string;
    forward_pe_next_year?: string;
}

export const fetchShareFloat = async (symbol: string): Promise<ShareFloat> => {
    const { data } = await apiClient.get<ShareFloat>(`${BASE_URL}/${symbol}/share-float`);
    return data;
};

export const fetchValuationRatios = async (
    symbol: string,
): Promise<ValuationRatios> => {
    const { data } = await apiClient.get<ValuationRatios>(
        `${BASE_URL}/${symbol}/valuation-ratios`,
    );
    return data;
};
